<template>
  <div class="navbar">
    <hamburger
      id="hamburger-container"
      :is-active="sidebar.opened"
      class="hamburger-container"
      @toggleClick="toggleSideBar"
    />
    <breadcrumb id="breadcrumb-container" class="breadcrumb-container"/>
    <div v-if="userInfo&&userInfo.type=='USER'" class="right-menu">
      <!-- 常驻求评入口：不弹窗、不打扰，点开即跳 App Store 评价页 -->
      <el-tooltip content="Happy with us? Leave a review" placement="bottom" :open-delay="300">
        <div
          class="review-entry"
          role="button"
          tabindex="0"
          aria-label="Leave us a review on the Shopify App Store"
          @click="openReview"
          @keyup.enter="openReview"
        >
          <i class="el-icon-star-off"></i>
          <span class="review-entry-text">Review us</span>
        </div>
      </el-tooltip>
      <!-- 联系入口：邮箱 / 电话 / 官网询价表单，放在导航常驻，任何页面一键可达 -->
      <el-tooltip content="Need help? Talk to us" placement="bottom" :open-delay="300">
        <div
          class="contact-entry"
          role="button"
          tabindex="0"
          aria-label="Contact Honest FulPhilment support"
          @click="contactVisible = true"
          @keyup.enter="contactVisible = true"
        >
          <i class="el-icon-chat-dot-round"></i>
          <span class="contact-entry-text">Contact us</span>
        </div>
      </el-tooltip>
      <div class="note">
        <el-dropdown trigger="click">
          <div
            class="avatar-wrapper"
            aria-haspopup="list"
            aria-controls="dropdown-menu-2922"
            role="button"
            tabindex="0"
          >
            <span v-if="notifyUnread" class="notify-txt">Unread notifications</span>
            <el-badge
              :hidden="!notifyUnread"
              is-dot
              :value="12"
              class="notify-dot"
            >
              <svg-icon icon-class="note" class="icon-note"></svg-icon>
            </el-badge>
          </div>
          <el-dropdown-menu slot="dropdown" class="notify-menu">
            <div class="notify-popup">
              <div class="notify-popup-header">
                <span class="title">System Notification</span>
                <div class="actions">
                  <el-link :underline="false" @click="notifyMarkAllRead">Mark all read</el-link>
                  <el-link :underline="false" @click="notifyClear">Clear</el-link>
                </div>
              </div>
              <div class="notify-popup-body">
                <div
                  v-for="item in notifys"
                  :key="item.id"
                  class="notify-item"
                  @click="clickNotifyItem(item.id)"
                >
                  <span class="dot" :class="{ 'dot-hidden': item.notifyLogId }"></span>
                  <div class="item-content">
                    <div class="item-title">{{ item.title }}</div>
                    <div class="item-meta">
                      <span class="type">{{ notifyTypes[item.notifyType] }}</span>
                      <span class="time">{{ item.createTime | parseTime("{y}-{m}-{d}") }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="!notifys.length" class="notify-empty">No notifications</div>
              </div>
            </div>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <div class="avatar-wrapper">
        <div class="information">
          <p>
            <span class="text-name" style="background: rgb(186, 116, 243)">{{
                name[0].toUpperCase()
              }}</span>
          </p>
        </div>
        <span class="user-name">{{ name }}</span>
      </div>
    </div>

    <el-dialog
      :visible.sync="contactVisible"
      width="440px"
      append-to-body
      custom-class="contact-dialog"
    >
      <div slot="title" class="contact-title">Contact us</div>
      <div class="contact-body">
        <p class="contact-lead">
          Questions about an order, a quote or your balance? Send us a message and we reply by
          email, usually within one business day (Mon–Sat 09:00–18:00 Beijing time).
        </p>
        <el-form :model="contactForm" label-position="top" class="contact-form" @submit.native.prevent>
          <el-form-item label="Subject">
            <el-input
              v-model="contactForm.subject"
              maxlength="200"
              placeholder="Order, quote, balance, shipping…"
            ></el-input>
          </el-form-item>
          <el-form-item label="Message">
            <el-input
              type="textarea"
              :rows="4"
              v-model="contactForm.content"
              maxlength="5000"
              placeholder="Tell us what you need. Include the order number if it is about a specific order."
            ></el-input>
          </el-form-item>
          <div class="contact-actions">
            <el-button
              type="primary"
              :loading="contactSubmitting"
              @click="submitContactMessage"
            >Send message</el-button>
            <span class="contact-hint">We reply to your account email.</span>
          </div>
        </el-form>
        <el-divider class="contact-divider">or reach us directly</el-divider>
        <a class="contact-row" :href="mailtoHref">
          <i class="el-icon-message"></i>
          <div>
            <b>Email support</b>
            <span>{{ supportEmail }}</span>
          </div>
        </a>
        <a class="contact-row" href="tel:+18446190365">
          <i class="el-icon-phone"></i>
          <div>
            <b>Call us</b>
            <span>+1 (844) 619-0365</span>
          </div>
        </a>
        <a
          class="contact-row"
          href="https://honestfulphilment.com/contact/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i class="el-icon-document"></i>
          <div>
            <b>Request a quote</b>
            <span>Send product links and get a landed cost</span>
          </div>
        </a>
      </div>
      <div slot="footer">
        <el-button @click="contactVisible = false">Close</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import Breadcrumb from "@//components/Breadcrumb";
import Hamburger from "@//components/Hamburger";
import ErrorLog from "@//components/ErrorLog";
import Loading from "@//components/Loading";
import * as dashboardApi from "@//api/dashboard";
import { openReviewPage } from "@/utils/app-review";
import { submit as submitAppMessage } from "@//api/app-message";

export default {
  props: ["notifyUnread"],
  components: {
    Breadcrumb,
    Hamburger,
    ErrorLog,
    Loading,
  },
  computed: {
    ...mapGetters(["sidebar", "avatar", "device", "name", "userInfo"]),
    userId() {
      const info = this.userInfo || {};
      return info.id || info.userId || null;
    },
    // 预填主题与正文，客服看到账号和当前页面，少一轮来回
    mailtoHref() {
      const info = this.userInfo || {};
      const who = info.email || info.username || "";
      const subject = encodeURIComponent("Support request" + (who ? " – " + who : ""));
      const body = encodeURIComponent(
        "Hi Honest FulPhilment team,\n\n" +
          "(Describe the issue and include the order number if relevant.)\n\n" +
          "Account: " + who + "\n" +
          "Page: " + (this.$route ? this.$route.fullPath : "") + "\n"
      );
      return "mailto:" + this.supportEmail + "?subject=" + subject + "&body=" + body;
    },
  },
  created() {
    if (this.userInfo && this.userInfo.type == 'USER') {
      this.loadNotify();
    }
    // 引导 / onboarding 里点「Contact us」时，由 Navbar 统一打开弹窗
    this.$root.$on("app:open-contact", this.openContact);
  },
  beforeDestroy() {
    this.$root.$off("app:open-contact", this.openContact);
  },
  data() {
    return {
      contactVisible: false,
      contactSubmitting: false,
      contactForm: {subject: "", content: ""},
      supportEmail: "support@honestfulphilment.com",
      notifys: [],
      notifyTypes: {
        SYS: "System notification",
        EMERGENCY: "Emergency notification",
        IMPORTANT: "Important reminder",
        HOLIDAY: "Holidays notification",
        NEW_FEATURE: "New feature",
        FEATURE_UPDATE: "Feature update",
        STOCK_ALERT: "Stock alert",
        INVENTORY_CHECK_ALERT: "Inventory check alert",
        REFUND: "Refund",
        SOURCE: "Sourcing request",
        BALANCE_ALERT: "Low balance alert",
        RATE_SYNC: "Shipping rate alert",
      },
    };
  },
  methods: {
    toggleSideBar() {
      this.$store.dispatch("app/toggleSideBar");
    },
    openReview() {
      openReviewPage(this.userId, "navbar");
    },
    openContact() {
      this.contactVisible = true;
    },
    submitContactMessage() {
      const subject = (this.contactForm.subject || "").trim();
      const content = (this.contactForm.content || "").trim();
      if (!subject) {
        this.$message.warning("Please add a subject.");
        return;
      }
      if (!content) {
        this.$message.warning("Please write your message.");
        return;
      }
      this.contactSubmitting = true;
      submitAppMessage({
        subject,
        content,
        page: this.$route ? this.$route.fullPath : ""
      })
        .then(() => {
          this.$message.success("Message sent. We will reply by email.");
          this.contactForm = {subject: "", content: ""};
          this.contactVisible = false;
        })
        .catch(() => {})
        .finally(() => {
          this.contactSubmitting = false;
        });
    },
    async logout() {
      await this.$store.dispatch("user/logout");
      this.$router.push(`/login?redirect=${this.$route.fullPath}`);
    },
    async loadNotify() {
      dashboardApi.notifyList({pageSize: 20}).then(({data}) => {
        this.notifys = data;
      });
    },
    notifyClear() {
      dashboardApi.notifyClear().then(() => {
        this.$message.success("Success");
        this.loadNotify();
      });
    },
    notifyMarkAllRead() {
      dashboardApi.notifyMarkAllRead().then(() => {
        this.$message.success("Success");
        this.loadNotify();
      });
    },
    clickNotifyItem(id) {
      this.$emit("clickNotifyItem", id);
    },
    notifyRead(id) {
      dashboardApi.notifyRead({id}).then(() => {
        const find = this.notifys.filter((item) => item.id == id);
        if (find.length == 0) {
          return;
        }
        const notify = find[0];
        this.$set(notify, "notifyLogId", "read");
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@//styles/variables.scss";

.notify-dot {
  margin-top: 10px;
  line-height: 30px;
}

.navbar {
  height: 50px;
  overflow: hidden;
  position: relative;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);

  .hamburger-container {
    line-height: 46px;
    height: 100%;
    float: left;
    cursor: pointer;
    transition: background 0.3s;
    -webkit-tap-highlight-color: transparent;

    &:hover {
      background: rgba(0, 0, 0, 0.025);
    }
  }

  .breadcrumb-container {
    float: left;
  }

  .errLog-container {
    display: inline-block;
    vertical-align: top;
  }

  .right-menu {
    float: right;
    height: 100%;
    line-height: 50px;
    // width: 280px;
    margin-right: 10px;
    display: flex;
    align-items: center;

    &:focus {
      outline: none;
    }

    /* 常驻求评入口：低调、不抢通知和头像的位置 */
    .contact-entry {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 100%;
      padding: 0 10px;
      font-size: 13px;
      color: #5b6b7c;
      cursor: pointer;
      transition: color 0.2s;
    }

    .contact-entry:hover {
      color: var(--theme, #ed1c24);
    }

    .contact-entry-text {
      font-weight: 600;
    }

    .review-entry {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 100%;
      padding: 0 10px;
      font-size: 13px;
      color: #9a8a5c;
      cursor: pointer;
      transition: color 0.2s;
      user-select: none;

      &:hover,
      &:focus {
        color: #e6a23c;
        outline: none;
      }

      i {
        font-size: 16px;
      }
    }

    .right-menu-item {
      display: inline-block;
      padding: 0 8px;
      height: 100%;
      font-size: 18px;
      color: #5a5e66;
      vertical-align: text-bottom;

      &.hover-effect {
        cursor: pointer;
        transition: background 0.3s;

        &:hover {
          background: rgba(0, 0, 0, 0.025);
        }
      }
    }

    .avatar-container {
      margin-right: 30px;

      .avatar-wrapper {
        margin-top: 5px;
        position: relative;


        .user-avatar {
          cursor: pointer;
          width: 40px;
          height: 40px;
          border-radius: 10px;
        }

        .el-icon-caret-bottom {
          cursor: pointer;
          position: absolute;
          right: -20px;
          top: 25px;
          font-size: 12px;
        }
      }
    }
  }
}

.notify-txt {
  margin-right: 8px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  color: #ed1c24;
  background: #fef0ef;
  border: 1px solid #f8cdc8;
  border-radius: 12px;
  cursor: pointer;
}

.note {
  margin-right: 15px;
  display: flex;
  align-content: center;

  .icon-note {
    font-size: 22px;
    cursor: pointer;
  }
}

.avatar-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  height: 100%;
}

.notify-menu {
  padding: 0 !important;
  min-width: 360px;
  max-width: 360px;
}

.notify-popup {
  font-size: 14px;
  color: #303133;

  .notify-popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;

    .title {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }

    .actions {
      display: flex;
      gap: 14px;

      .el-link {
        font-size: 12px;
      }
    }
  }

  .notify-popup-body {
    max-height: 360px;
    overflow-y: auto;
    padding: 4px 0;
  }

  .notify-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 16px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #f5f7fa;
    }

    .dot {
      width: 8px;
      height: 8px;
      background: #ed1c24;
      border-radius: 50%;
      margin-top: 6px;
      flex-shrink: 0;
    }

    .dot-hidden {
      background: #fff;
      border: 1px solid #dcdfe6;
    }

    .item-content {
      flex: 1;
      min-width: 0;

      .item-title {
        font-size: 13px;
        color: #303133;
        line-height: 1.4;
        word-break: break-word;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .item-meta {
        margin-top: 4px;
        display: flex;
        align-items: center;
        gap: 8px;

        .type {
          font-size: 11px;
          color: #909399;
          background: #f5f7fa;
          padding: 1px 6px;
          border-radius: 3px;
        }

        .time {
          font-size: 11px;
          color: #c0c4cc;
        }
      }
    }
  }

  .notify-empty {
    padding: 40px 0;
    text-align: center;
    color: #909399;
    font-size: 13px;
  }
}

.information {
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-name {
  font-size: 14px;
  color: #222;
  margin-left: 10px;
}

.text-name {
  width: 32px;
  height: 32px;
  background: #d8d8d8;
  border-radius: 50%;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fefefe;
  font-size: 16px;
}

/* 窄屏只留星标，避免和通知/头像挤在一起 */
@media (max-width: 768px) {
  .right-menu .review-entry-text {
    display: none;
  }
}
</style>

<style lang="scss">
.contact-dialog {
  border-radius: 10px;
  max-width: calc(100vw - 32px);
  margin-top: 8vh !important;

  .el-dialog__header {
    padding: 18px 22px 6px;
    border-bottom: none;
  }

  .contact-title {
    font-size: 17px;
    font-weight: 700;
    color: #1f2d3d;
  }

  .contact-body {
    padding: 4px 22px 8px;
  }

  .contact-lead {
    margin: 0 0 16px;
    font-size: 13px;
    line-height: 1.6;
    color: #6b7785;
  }

  .contact-form {
    .el-form-item {
      margin-bottom: 12px;
    }

    .el-form-item__label {
      padding-bottom: 2px;
      font-size: 12.5px;
      color: #6b7785;
      line-height: 1.4;
    }
  }

  .contact-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 4px 0 6px;
  }

  .contact-hint {
    font-size: 12px;
    color: #9aa4b0;
  }

  .contact-divider {
    margin: 14px 0 12px;

    .el-divider__text {
      font-size: 12px;
      color: #9aa4b0;
      background: #fff;
    }
  }

  .contact-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    margin-bottom: 10px;
    border: 1px solid #eceff3;
    border-radius: 8px;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.2s, background 0.2s;

    i {
      font-size: 20px;
      color: #ed1c24;
    }

    b {
      display: block;
      font-size: 14px;
      color: #1f2d3d;
    }

    span {
      display: block;
      font-size: 12.5px;
      color: #6b7785;
      margin-top: 2px;
      word-break: break-all;
    }

    &:hover {
      border-color: #ed1c24;
      background: #fff7f7;
    }
  }

  .el-dialog__footer {
    padding: 6px 22px 18px;
    text-align: right;
  }
}
</style>
